import { Badge } from "@ui/Atoms/Badge/Badge";
import { IconButton } from "@ui/Atoms/IconButton/IconButton";
import { Link } from "@ui/Atoms/Link/Link";
import { Score } from "@ui/Atoms/Score/Score";
import { IconArrowUpRight } from "@ui/Icons/IconArrowUpRight";
import { IconChevronLeft } from "@ui/Icons/IconChevronLeft";
import { IconClose } from "@ui/Icons/IconClose";
import { IconExpand } from "@ui/Icons/IconExpand";
import { IconHeart } from "@ui/Icons/IconHeart";
import { IconInfo } from "@ui/Icons/IconInfo";
import { IconMapPin } from "@ui/Icons/IconMapPin";
import { IconPencil } from "@ui/Icons/IconPencil";
import { IconSearch } from "@ui/Icons/IconSearch";
import { IconShare } from "@ui/Icons/IconShare";
import { EmbedCard } from "@ui/Molecules/Embed/Card/EmbedCard";
import { InfoRows } from "@ui/Molecules/Info/InfoRows";
import { SOCIAL_PLATFORM_ICON } from "@ui/Molecules/Social/SocialChips";
import { AppShell } from "@ui/Organisms/Layout/AppShell";
import { socialEmbedUrl } from "@ui/utils";
import { useState } from "react";
import { DetailEmbedMedia } from "./DetailEmbedMedia";
import type { PlaceDetailTemplateProps } from "./types";

export function PlaceDetailTemplate({
	topBar,
	tabBar,
	translations: t,
	name,
	area,
	backLabel,
	onBack,
	embed,
	badges,
	score,
	info,
	tags,
	embeds,
	saved,
	onToggleSave,
	showSave = true,
	routeHref,
	onShare,
	onEdit,
	onReport,
}: PlaceDetailTemplateProps) {
	const [expanded, setExpanded] = useState(false);

	const badgeRow = (
		<div className="flex flex-wrap gap-2">
			{badges.price && <Badge tone="outline">{badges.price}</Badge>}
			{badges.open && <Badge tone="open">{t.openLabel}</Badge>}
			{badges.walk && <Badge tone="outline">{badges.walk}</Badge>}
		</div>
	);

	const platform = embed.platform;
	const EmbedPlatformIcon = platform ? SOCIAL_PLATFORM_ICON[platform] : null;
	const embedIframeSrc =
		embed.videoUrl || !platform
			? null
			: socialEmbedUrl(platform, embed.sourceUrl);

	const embedLinkCard = platform && EmbedPlatformIcon && (
		<a
			href={embed.sourceUrl}
			target="_blank"
			rel="noopener noreferrer"
			className="flex items-center gap-3 rounded-[var(--radius-md)] bg-[var(--surface-elevated)] px-4 py-3.5"
		>
			<span className="grid h-9 w-9 shrink-0 place-items-center rounded-[var(--radius-pill)] bg-[var(--surface-default)]">
				<EmbedPlatformIcon size={17} color="var(--content-primary)" />
			</span>
			<span className="min-w-0 flex-1">
				<span className="block text-[14px] font-bold">
					{embed.ago ? t.embedAge(embed.ago) : t.embedLink(platform)}
				</span>
				<span className="block text-[12.5px] text-[var(--content-muted)]">
					{t.embedMeta(embed.handle, embed.views ?? "")}
				</span>
			</span>
			<div className="flex items-center gap-2.5 shrink-0">
				<span className="text-[13px] font-bold text-[var(--accent-500)]">
					{t.embedLink(platform)}
				</span>
				<IconArrowUpRight size={13} color="var(--accent-500)" />
			</div>
		</a>
	);

	const tagsSection = tags.length > 0 && (
		<section className="flex flex-col gap-3">
			<h2
				className="m-0 text-[15px] font-extrabold tracking-[-0.025em] md:text-[20px]"
				style={{ fontFamily: "var(--font-display)" }}
			>
				{t.tagsTitle}
			</h2>
			<div className="flex flex-wrap gap-2">
				{tags.map((tag) => (
					<Link
						key={tag.label}
						href={tag.href}
						className="inline-flex items-center gap-1.5 rounded-[var(--radius-pill)] border border-[var(--border-strong)] bg-[var(--surface-default)] px-3.5 py-2 text-[13px] font-bold"
					>
						<IconSearch size={12} color="var(--content-faint)" />
						{tag.label}
					</Link>
				))}
			</div>
		</section>
	);

	const embedsSection = embeds.length > 0 && (
		<section className="flex flex-col gap-3">
			<div className="flex items-baseline gap-3">
				<h2
					className="m-0 text-[15px] font-extrabold tracking-[-0.025em] md:text-[20px]"
					style={{ fontFamily: "var(--font-display)" }}
				>
					{t.embedsTitle}
				</h2>
				<span className="text-[13px] text-[var(--content-muted)]">
					{t.embedsSubtitle}
				</span>
			</div>
			<div className="flex gap-3.5 overflow-x-auto scrollbar-hide -mx-5 px-5 md:mx-0 md:px-0">
				{embeds.map((r) => (
					<button
						key={r.id}
						type="button"
						onClick={() => setExpanded(true)}
						className="shrink-0"
					>
						<EmbedCard {...r} variant="rail" emptyLabel={t.emptyEmbedLabel} />
					</button>
				))}
			</div>
		</section>
	);

	const reportLine = onReport && (
		<button
			type="button"
			onClick={onReport}
			className="flex items-center gap-2.5 border-t border-[var(--border-strong)] pt-5 text-left"
		>
			<IconInfo size={15} color="var(--content-faint)" />
			<span className="text-[13px] font-semibold text-[var(--content-muted)]">
				{t.reportPrompt}{" "}
				<span className="font-bold text-[var(--accent-500)]">
					{t.reportAction}
				</span>
			</span>
		</button>
	);

	return (
		<AppShell topBar={topBar} tabBar={tabBar}>
			{/* Desktop */}
			<div className="mx-auto hidden max-w-[1360px] px-10 pb-24 pt-7 md:block">
				<button
					type="button"
					onClick={onBack}
					className="mb-5 inline-flex items-center gap-2 text-[13.5px] font-bold text-[var(--content-muted)]"
				>
					<IconChevronLeft size={16} color="var(--content-muted)" />
					{backLabel}
				</button>

				<div className="grid items-start gap-14 md:grid-cols-[minmax(0,440px)_minmax(0,1fr)]">
					<div className="sticky top-[100px] flex flex-col gap-3.5">
						<button
							type="button"
							onClick={() => setExpanded(true)}
							className="relative block aspect-[9/16] overflow-hidden rounded-[var(--radius-lg)] bg-[var(--surface-inverted)]"
						>
							<DetailEmbedMedia
								embed={embed}
								name={name}
								emptyLabel={t.emptyEmbedLabel}
							/>
							<span className="absolute left-3.5 top-3.5 inline-flex items-center gap-1.5 rounded-[var(--radius-pill)] bg-[rgb(17_17_17_/_0.6)] px-3 py-1.5 text-[11px] font-bold tracking-[0.04em] text-white">
								{EmbedPlatformIcon && (
									<EmbedPlatformIcon size={13} color="#fff" />
								)}
								{t.embedBadge}
							</span>
							<span className="absolute right-3.5 top-3.5 grid h-9 w-9 place-items-center rounded-[var(--radius-pill)] bg-[var(--surface-glass)] text-white">
								<IconExpand size={16} color="#fff" />
							</span>
						</button>
						{embedLinkCard}
					</div>

					<div className="flex flex-col gap-8">
						<div className="flex flex-col gap-4">
							<div className="flex items-start justify-between gap-8">
								<div className="flex flex-col gap-1.5">
									<h1
										className="m-0 text-[46px] font-extrabold leading-[1.04] tracking-[-0.03em]"
										style={{ fontFamily: "var(--font-display)" }}
									>
										{name}
									</h1>
									<div className="text-[16px] text-[var(--content-muted)]">
										{area}
									</div>
								</div>
								<div className="flex shrink-0 gap-2">
									<a
										href={routeHref}
										target="_blank"
										rel="noopener noreferrer"
										className="flex h-11 items-center gap-2 rounded-[var(--radius-sm)] bg-[var(--surface-inverted)] px-4 text-[14px] font-bold text-[var(--content-inverted)]"
									>
										<IconMapPin size={16} color="#fff" />
										{t.routeLabel}
									</a>
									{showSave && (
										<IconButton
											label={saved ? t.savedLabel : t.saveLabel}
											variant="outline"
											shape="square"
											size={44}
											onClick={onToggleSave}
										>
											<IconHeart
												size={19}
												color={
													saved ? "var(--accent-500)" : "var(--content-primary)"
												}
												fill={saved ? "var(--accent-500)" : "none"}
											/>
										</IconButton>
									)}
									<IconButton
										label={t.shareLabel}
										variant="outline"
										shape="square"
										size={44}
										onClick={onShare}
									>
										<IconShare size={17} />
									</IconButton>
									{onEdit && (
										<IconButton
											label={t.editLabel}
											variant="outline"
											shape="square"
											size={44}
											onClick={onEdit}
										>
											<IconPencil size={17} />
										</IconButton>
									)}
								</div>
							</div>
							{badgeRow}
						</div>

						<div className="grid items-start gap-5 md:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
							{score && (
								<div className="flex flex-col gap-3 rounded-[var(--radius-lg)] border border-[var(--border-default)] bg-[var(--surface-default)] p-6">
									<div className="flex items-center justify-between">
										<div className="text-[11px] font-bold uppercase tracking-[0.06em] text-[var(--content-muted)]">
											{score.eyebrow}
										</div>
										<Score value={score.value} />
									</div>
									<p className="m-0 text-[16px] leading-[1.62]">
										{score.verdict}
									</p>
								</div>
							)}
							<InfoRows rows={info} />
						</div>

						{tagsSection}
						{embedsSection}
						{reportLine}
					</div>
				</div>
			</div>

			{/* Mobile */}
			<div className="md:hidden">
				<div className="relative aspect-[9/12] w-full overflow-hidden bg-[var(--surface-inverted)]">
					<DetailEmbedMedia
						embed={embed}
						name={name}
						emptyLabel={t.emptyEmbedLabel}
					/>
					<div className="absolute inset-x-0 top-0 flex items-center justify-between px-5 pt-[calc(14px+env(safe-area-inset-top))]">
						<IconButton
							label={backLabel}
							variant="glass"
							size={36}
							onClick={onBack}
						>
							<IconChevronLeft size={18} color="#fff" />
						</IconButton>
						<div className="flex gap-2">
							<IconButton
								label={t.shareLabel}
								variant="glass"
								size={36}
								onClick={onShare}
							>
								<IconShare size={16} color="#fff" />
							</IconButton>
							{showSave && (
								<IconButton
									label={saved ? t.savedLabel : t.saveLabel}
									variant="glass"
									size={36}
									onClick={onToggleSave}
								>
									<IconHeart
										size={16}
										color="#fff"
										fill={saved ? "#fff" : "none"}
									/>
								</IconButton>
							)}
							<IconButton
								label={t.embedBadge}
								variant="glass"
								size={36}
								onClick={() => setExpanded(true)}
							>
								<IconExpand size={15} color="#fff" />
							</IconButton>
						</div>
					</div>
					<div className="absolute inset-x-0 bottom-0 bg-[linear-gradient(to_top,rgb(0_0_0_/_0.82),transparent)] px-5 pb-4 pt-11">
						<div className="mb-1 flex items-center gap-1.5 text-[12px] font-bold text-[rgb(255_255_255_/_0.85)]">
							{EmbedPlatformIcon && (
								<EmbedPlatformIcon size={13} color="#fff" />
							)}
							{t.embedBadge}
						</div>
						<div
							className="text-[23px] font-extrabold leading-[1.2] tracking-[-0.025em] text-white"
							style={{ fontFamily: "var(--font-display)" }}
						>
							{name}
						</div>
						<div className="mt-0.5 text-[13px] text-[rgb(255_255_255_/_0.75)]">
							{area}
						</div>
					</div>
				</div>

				<div className="flex flex-col gap-5 px-5 pb-28 pt-5">
					{badgeRow}
					{embedLinkCard}
					{score && (
						<div className="flex flex-col gap-2.5 rounded-[var(--radius-sm)] border border-[var(--border-default)] p-[18px]">
							<div className="flex items-center justify-between">
								<div className="text-[11px] font-bold uppercase tracking-[0.06em] text-[var(--content-muted)]">
									{score.eyebrow}
								</div>
								<Score value={score.value} size="sm" />
							</div>
							<p className="m-0 text-[14.5px] leading-[1.6]">{score.verdict}</p>
						</div>
					)}
					<InfoRows rows={info} boxed={false} />
					{tagsSection}
					{embedsSection}
					{reportLine}
				</div>

				<div className="fixed inset-x-0 bottom-0 z-[850] flex gap-2.5 border-t border-[var(--border-default)] bg-[var(--surface-default)] px-5 pb-[calc(22px+env(safe-area-inset-bottom))] pt-3">
					<a
						href={routeHref}
						target="_blank"
						rel="noopener noreferrer"
						className="flex h-12 flex-1 items-center justify-center gap-2 rounded-[var(--radius-md)] bg-[var(--surface-inverted)] text-[14.5px] font-bold text-[var(--content-inverted)]"
					>
						<IconMapPin size={17} color="#fff" />
						{t.routeLabel}
					</a>
					{platform && EmbedPlatformIcon && (
						<a
							href={embed.sourceUrl}
							target="_blank"
							rel="noopener noreferrer"
							aria-label={t.embedLink(platform)}
							className="grid h-12 w-12 shrink-0 place-items-center rounded-[var(--radius-md)] border border-[var(--border-strong)]"
						>
							<EmbedPlatformIcon size={19} />
						</a>
					)}
				</div>
			</div>

			{expanded && (
				<div className="fixed inset-0 z-[1300] flex items-center justify-center bg-[rgb(8_8_7_/_0.94)] p-6 md:p-10">
					{embed.videoUrl ? (
						// biome-ignore lint/a11y/useMediaCaption: user-generated social clip
						<video
							controls
							autoPlay
							loop
							playsInline
							preload="auto"
							className="h-full max-w-full rounded-[var(--radius-lg)] bg-black"
						>
							<source src={embed.videoUrl} />
						</video>
					) : embedIframeSrc ? (
						<iframe
							src={embedIframeSrc}
							title={name}
							className="h-[80vh] max-h-[740px] w-[360px] max-w-full rounded-[var(--radius-lg)] bg-white"
							allow="autoplay; encrypted-media; picture-in-picture"
							allowFullScreen
						/>
					) : embed.poster ? (
						<img
							src={embed.poster}
							alt={name}
							className="max-h-full max-w-full rounded-[var(--radius-lg)]"
						/>
					) : null}
					<IconButton
						label={t.closeLabel}
						variant="glass"
						size={42}
						onClick={() => setExpanded(false)}
						style={{ position: "absolute", top: 28, right: 32 }}
					>
						<IconClose size={20} color="#fff" />
					</IconButton>
				</div>
			)}
		</AppShell>
	);
}
